import {
  type AddFilesToMemoReq,
  type AddFileToMemoReq,
  CreateAiReportReq,
  CreateAiReportResp,
  FileList,
  FileObject,
  Memo,
  MemoClassification,
  type MemoClassificationList,
  MemoComment,
  MemoCommentLog,
  type MemoCommentLogList,
  type MemoList,
  MemoStatus,
  type MemoStatusList,
  type RemoveFileFromMemoReq,
} from '@kalos/kalos-rpc';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fileToFileObject,
  imageFileToStagedFileObject,
  isImageForStaging,
  MEMO_STAGING_PREPARE_CONCURRENCY,
  runWithConcurrency,
} from '../../tools/fileUploadUtils';
import { getEnhancedMimeType, MemoClientService } from '../../tools/helpers';
import { queryKeys } from './constants';
import { type EntityFilter } from './utils';

/**
 * Normalizes a Memo object to ensure the files field is always present.
 * Prevents null/undefined errors when accessing memo.files in components.
 *
 * @param memo - The memo object to normalize
 * @returns The memo with a guaranteed files field
 */
const normalizeMemoFiles = (memo: Memo): Memo => ({
  ...memo,
  files: memo.files || FileList.create({ results: [], totalCount: 0 }),
});

export type MemoFilter = EntityFilter<Memo>;
export const useMemoQuery = ({
  enabled = true,
  filters,
  refreshInterval,
  refetchOnWindowFocus = true,
}: {
  enabled?: boolean;
  filters: MemoFilter;
  refreshInterval?: number;
  refetchOnWindowFocus?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.memoList.root, queryKeys.memoList.getMemoListHash(filters)],
    queryFn: async () => {
      const req = Memo.create(filters);
      const response = await MemoClientService.BatchGet(req);
      return response || { results: [], totalCount: 0 };
    },
    enabled,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus,
  });
};

export const useGetMemoQuery = ({
  enabled = true,
  filters,
}: {
  enabled?: boolean;
  filters: MemoFilter;
}) => {
  return useQuery({
    queryKey: [queryKeys.memo.root, queryKeys.memo.getMemoHash(filters)],
    queryFn: async () => {
      const req = Memo.create(filters);
      const response = await MemoClientService.Get(req);
      return response || Memo.create();
    },
    enabled,
  });
};

type UpdateMemoResponse = Memo;
export const useUpdateMemoMutation = (
  opts?: Parameters<
    typeof useMutation<
      UpdateMemoResponse,
      unknown,
      Parameters<typeof MemoClientService.Update>['0'],
      unknown
    >
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const response = await MemoClientService.Update(arg);
      return response || Memo.create();
    },
    onSuccess: (data, variables) => {
      // Make sure the files field is normalized
      const normalizedData = normalizeMemoFiles(data);
      // Check if this is a delete/reactivate operation (IsActive field change)
      const isActiveStatusChange = variables.fieldMask?.includes('IsActive');

      if (isActiveStatusChange) {
        // For delete/reactivate operations, remove all memo queries from cache to force fresh fetch
        // This ensures the table hits loading state and refreshes with correct data
        queryClient.removeQueries({
          queryKey: [queryKeys.memoList.root],
          exact: false, // This will remove all queries that start with memoList.root
        });

        // Also remove individual memo queries
        queryClient.removeQueries({
          queryKey: [queryKeys.memo.root],
          exact: false, // This will remove all queries that start with memo.root
        });
      } else {
        // For other operations (like status, classification, user assignment), use optimistic updates
        // Update the individual memo cache
        queryClient.setQueryData(
          [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: variables.id })],
          normalizedData,
        );

        // Update the memo in memo lists that might contain it
        queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
          if (!oldData || !oldData.results) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((memo) =>
              memo.id === variables.id ? { ...memo, ...normalizedData } : memo,
            ),
          };
        });
      }

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    onError: (error, variables) => {
      // Call any additional onError handlers provided in options
      if (opts?.onError) {
        opts.onError(error, variables, undefined as any);
      }
    },
    ...opts,
  });
};

type DeleteMemoResponse = Memo;
export const useDeleteMemoMutation = (
  opts?: Parameters<
    typeof useMutation<
      DeleteMemoResponse,
      unknown,
      Parameters<typeof MemoClientService.Delete>['0'],
      unknown
    >
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const response = await MemoClientService.Delete(arg);
      return response || Memo.create();
    },
    onSuccess: (data, variables) => {
      // Remove the deleted memo from any memo lists in the cache
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.filter((memo) => memo.id !== variables.id),
          totalCount: Math.max((oldData.totalCount || 0) - 1, 0),
        };
      });

      // Invalidate the individual memo query since it's been deleted
      queryClient.removeQueries({
        queryKey: [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: variables.id })],
      });

      // Invalidate event feed queries to refetch with the deleted memo removed
      queryClient.invalidateQueries({
        queryKey: [queryKeys.eventFeed.root],
      });

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

export type MemoCommentFilter = EntityFilter<MemoComment>;
export const useMemoCommentQuery = ({
  enabled = true,
  filters,
  refreshInterval,
}: {
  enabled?: boolean;
  filters: MemoCommentFilter;
  refreshInterval?: number;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.memoCommentList.root,
      queryKeys.memoCommentList.getMemoCommentListHash(filters),
    ],
    queryFn: async () => {
      const req = MemoComment.create(filters);
      const response = await MemoClientService.BatchGetMemoComment(req);
      return response?.results ?? [];
    },
    enabled,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
  });
};

type CreateMemoCommentResponse = MemoComment;
export const useCreateMemoCommentMutation = (
  opts?: Parameters<
    typeof useMutation<
      CreateMemoCommentResponse,
      unknown,
      Parameters<typeof MemoClientService.CreateMemoComment>['0'],
      unknown
    >
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const response = await MemoClientService.CreateMemoComment(arg);
      return response || MemoComment.create();
    },
    onSuccess: (data, variables) => {
      // Update the memo list cache to increment comment count
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.map((memo) => {
            if (memo.id === variables.memoId) {
              return {
                ...memo,
                // Increment the comment count if it exists, otherwise initialize to 1
                commentCount: (memo.commentCount || 0) + 1,
              };
            }
            return memo;
          }),
        };
      });

      // Update the comment list cache for the specific memo
      queryClient.setQueriesData<MemoComment[]>(
        { queryKey: [queryKeys.memoCommentList.root] },
        (oldData) => {
          if (oldData && Array.isArray(oldData)) {
            return [...oldData, data];
          }
          return oldData;
        },
      );

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

type UpdateMemoCommentResponse = MemoComment;
export const useUpdateMemoCommentMutation = (
  opts?: Parameters<
    typeof useMutation<
      UpdateMemoCommentResponse,
      unknown,
      Parameters<typeof MemoClientService.UpdateMemoComment>['0'],
      unknown
    >
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const response = await MemoClientService.UpdateMemoComment(arg);
      return response || MemoComment.create();
    },
    onSuccess: (data, variables) => {
      // If this is a restore operation (isActive being set to true), increment comment count
      if (variables.fieldMask?.includes('IsActive') && variables.isActive) {
        queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
          if (!oldData || !oldData.results) return oldData;

          return {
            ...oldData,
            results: oldData.results.map((memo) => {
              if (memo.id === variables.memoId) {
                return {
                  ...memo,
                  // Increment the comment count when restoring a comment
                  commentCount: (memo.commentCount || 0) + 1,
                };
              }
              return memo;
            }),
          };
        });
      }

      // Update the comment list cache
      queryClient.setQueriesData<MemoComment[]>(
        { queryKey: [queryKeys.memoCommentList.root] },
        (oldData) => {
          if (oldData && Array.isArray(oldData)) {
            return oldData.map((comment) => {
              if (comment.id === data?.id) {
                return data || variables;
              }
              return comment;
            });
          }
          return oldData;
        },
      );

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

type DeleteMemoCommentResponse = MemoComment;
export const useDeleteMemoCommentMutation = (
  opts?: Parameters<
    typeof useMutation<
      DeleteMemoCommentResponse,
      unknown,
      Parameters<typeof MemoClientService.DeleteMemoComment>['0'],
      unknown
    >
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      const response = await MemoClientService.DeleteMemoComment(arg);
      return response || MemoComment.create();
    },
    onSuccess: (data, variables) => {
      // Update the memo list cache to decrement comment count
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.map((memo) => {
            if (memo.id === variables.memoId) {
              return {
                ...memo,
                // Decrement the comment count but don't go below 0
                commentCount: Math.max((memo.commentCount || 1) - 1, 0),
              };
            }
            return memo;
          }),
        };
      });

      // Update the comment list cache to mark comment as inactive
      queryClient.setQueriesData<MemoComment[]>(
        { queryKey: [queryKeys.memoCommentList.root] },
        (oldData) => {
          if (oldData && Array.isArray(oldData)) {
            return oldData.map((comment) => {
              if (comment.id === variables.id) {
                return { ...comment, isActive: false };
              }
              return comment;
            });
          }
          return oldData;
        },
      );

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

export type MemoCommentLogFilter = EntityFilter<MemoCommentLog>;
export const useMemoCommentLogQuery = ({
  enabled = true,
  filters,
  refreshInterval,
}: {
  enabled?: boolean;
  filters: MemoCommentLogFilter;
  refreshInterval?: number;
  onSuccess?: (data: MemoCommentLogList) => void;
}) => {
  return useQuery({
    queryKey: [
      queryKeys.memoCommentLogList.root,
      queryKeys.memoCommentLogList.getMemoCommentLogListHash(filters),
    ],
    queryFn: () => {
      const req = MemoCommentLog.create(filters);
      return MemoClientService.BatchGetMemoCommentLog(req);
    },
    enabled,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
  });
};

type CreateMemoCommentLogResponse = Awaited<
  ReturnType<typeof MemoClientService.CreateMemoCommentLog>
>;
export const useCreateMemoCommentLogMutation = (
  opts?: Parameters<
    typeof useMutation<
      CreateMemoCommentLogResponse,
      unknown,
      Parameters<typeof MemoClientService.CreateMemoCommentLog>['0'],
      unknown
    >
  >['0'],
) => {
  return useMutation({
    mutationFn: (arg) => MemoClientService.CreateMemoCommentLog(arg),
    ...opts,
  });
};

type MemoStatusFilter = EntityFilter<MemoStatus>;
export const useMemoStatusQuery = <TSelectData = MemoStatusList,>({
  enabled = true,
  filters = {},
  refreshInterval,
  select,
}: {
  enabled?: boolean;
  filters?: MemoStatusFilter;
  refreshInterval?: number;
  select?: (data: MemoStatusList) => TSelectData;
} = {}) => {
  return useQuery({
    queryKey: [
      queryKeys.memoStatusList.root,
      queryKeys.memoStatusList.getMemoStatusListHash(filters),
    ],
    queryFn: () => {
      const req = MemoStatus.create(filters);
      return MemoClientService.BatchGetMemoStatus(req);
    },
    enabled,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
    select,
  });
};

type MemoClassificationFilter = EntityFilter<MemoClassification>;
export const useMemoClassificationQuery = <TSelect = MemoClassificationList,>({
  enabled = true,
  filters = {},
  refreshInterval,
  select,
}: {
  enabled?: boolean;
  filters?: MemoClassificationFilter;
  refreshInterval?: number;
  select?: (data: MemoClassificationList) => TSelect;
} = {}) => {
  return useQuery({
    queryKey: [
      queryKeys.memoClassificationList.root,
      queryKeys.memoClassificationList.getMemoClassificationListHash(filters),
    ],
    queryFn: () => {
      const req = MemoClassification.create(filters);
      return MemoClientService.BatchGetMemoCategory(req);
    },
    enabled,
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: true,
    select,
  });
};

type CreateFullMemoResponse = Memo;
export const useCreateFullMemoMutation = (
  opts?: Parameters<
    typeof useMutation<
      CreateFullMemoResponse,
      unknown,
      Parameters<typeof MemoClientService.CreateFullMemo>['0'],
      unknown
    >
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (arg) => {
      // Create the memo first
      const response = await MemoClientService.CreateFullMemo(arg);
      if (!response) return Memo.create();

      // Now fetch the complete memo with files (include all file types)
      const req = Memo.create({
        id: response.id,
        includeVideoFiles: true,
        includeUnsupportedMobileFiles: true,
      });
      const fullMemo = await MemoClientService.Get(req);
      return fullMemo || response;
    },
    onSuccess: (data, variables) => {
      // Make sure we always explicitly set the files field, even for memos without files
      const newMemoWithNormalizedFiles = normalizeMemoFiles(data);

      // Add the newly fetched memo with complete data to all relevant memo lists
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: [newMemoWithNormalizedFiles, ...oldData.results],
          totalCount: (oldData.totalCount || 0) + 1,
        };
      });

      // Also set the individual memo data with normalized files field
      queryClient.setQueryData([queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: data.id })], {
        ...data,
        files: data.files || FileList.create({ results: [], totalCount: 0 }),
      });

      // Invalidate event feed queries so they refetch with the new memo
      queryClient.invalidateQueries({
        queryKey: [queryKeys.eventFeed.root],
      });

      // Internal job notes are mirrored into the legacy Event.logNotes field
      // server-side on create, so refetch the event too
      queryClient.invalidateQueries({
        queryKey: [queryKeys.events.root],
      });

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

// New hooks for adding and removing files
export type AddFileToMemoParams = {
  memoId: number;
  file: File; // The file to upload
};

export type RemoveFileFromMemoParams = {
  memoId: number;
  fileId: number;
};

type AddFileToMemoResponse = Memo;
export const useAddFileToMemoMutation = (
  opts?: Parameters<
    typeof useMutation<AddFileToMemoResponse, unknown, AddFileToMemoParams, unknown>
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddFileToMemoParams) => {
      const { memoId, file } = params;

      // Convert the File to a FileObject
      const arrayBuffer = await file.arrayBuffer();
      const fileData = new Uint8Array(arrayBuffer);
      const mimeType = getEnhancedMimeType(file.name, fileData);

      const fileObject = FileObject.create({
        bucket: 'testbuckethelios',
        key: file.name,
        mimeType,
        data: fileData,
      });

      const request: AddFileToMemoReq = {
        memoId,
        file: fileObject,
      };

      const response = await MemoClientService.AddFileToMemo(request);
      return response || Memo.create();
    },
    onSuccess: (data, variables) => {
      // Ensure files are normalized
      const normalizedData = normalizeMemoFiles(data);

      // Update the memo in all lists with the new file
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.map((memo) => {
            if (memo.id === variables.memoId) {
              // Use only the files from the response to avoid duplicates
              return {
                ...memo,
                files: normalizedData.files,
              };
            }
            return memo;
          }),
        };
      });

      // Update the individual memo if it's in the cache
      // with the complete data from the response
      queryClient.setQueryData(
        [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: variables.memoId })],
        normalizedData,
      );

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

type RemoveFileFromMemoResponse = Memo;
export const useRemoveFileFromMemoMutation = (
  opts?: Parameters<
    typeof useMutation<RemoveFileFromMemoResponse, unknown, RemoveFileFromMemoParams, unknown>
  >['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: RemoveFileFromMemoParams) => {
      const request: RemoveFileFromMemoReq = {
        memoId: params.memoId,
        fileId: params.fileId,
      };

      const response = await MemoClientService.RemoveFileFromMemo(request);
      return response || Memo.create();
    },
    onSuccess: (data, variables) => {
      const filterFileFromMemo = (memo: Memo): Memo => {
        if (memo.id !== variables.memoId || !memo.files?.results) return memo;
        return {
          ...memo,
          files: {
            ...memo.files,
            results: memo.files.results.filter((file) => file.id !== variables.fileId),
          },
        };
      };

      // Update all memo list caches (used by useMemoQuery)
      queryClient.setQueriesData(
        { queryKey: [queryKeys.memoList.root] },
        (oldData: { results: Memo[]; totalCount: number } | undefined) => {
          if (!oldData || !oldData.results) return oldData;
          return {
            ...oldData,
            results: oldData.results.map(filterFileFromMemo),
          };
        },
      );

      // Update individual memo cache (used by useGetMemoQuery)
      queryClient.setQueryData(
        [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: variables.memoId })],
        (oldData: Memo | undefined) => {
          if (!oldData) return oldData;
          return filterFileFromMemo(oldData);
        },
      );

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

export type AddFilesToMemoParams = {
  memoId: number;
  files: File[];
};

export const useAddFilesToMemoMutation = (
  opts?: Parameters<typeof useMutation<Memo, unknown, AddFilesToMemoParams, unknown>>['0'],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: AddFilesToMemoParams) => {
      const { memoId, files } = params;

      // Same staging as CreateFullMemo: downscale raster images client-side and cap
      // concurrency. Full-resolution images sent inline make the backend decode and
      // generate variants for every file at once, which kills the container.
      const fileObjects = await runWithConcurrency(files, MEMO_STAGING_PREPARE_CONCURRENCY, (f) =>
        isImageForStaging(f) ? imageFileToStagedFileObject(f) : fileToFileObject(f),
      );

      const request: AddFilesToMemoReq = {
        memoId,
        files: fileObjects,
      };

      const response = await MemoClientService.AddFilesToMemo(request);
      return response || Memo.create();
    },
    onSuccess: (data, variables) => {
      // Ensure files are normalized
      const normalizedData = normalizeMemoFiles(data);

      // Update all memo lists using only the server response files
      queryClient.setQueriesData<MemoList>({ queryKey: [queryKeys.memoList.root] }, (oldData) => {
        if (!oldData || !oldData.results) return oldData;

        return {
          ...oldData,
          results: oldData.results.map((memo) => {
            if (memo.id === variables.memoId) {
              // Always use the complete updated memo from the server response
              // to avoid duplicate files
              return {
                ...memo,
                files: normalizedData.files,
              };
            }
            return memo;
          }),
        };
      });

      // Update the individual memo cache directly with the complete response
      queryClient.setQueryData(
        [queryKeys.memo.root, queryKeys.memo.getMemoHash({ id: variables.memoId })],
        normalizedData,
      );

      // Call any additional onSuccess handlers provided in options
      if (opts?.onSuccess) {
        opts.onSuccess(data, variables, undefined as any);
      }
    },
    ...opts,
  });
};

// JobMemoSummary hooks
export type JobMemoSummaryParams = {
  jobIds: number[];
};

export const useJobMemoSummaryQuery = ({
  jobIds,
  enabled = true,
}: {
  jobIds: number[];
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.jobMemoSummary.root, jobIds],
    queryFn: async () => {
      // Add a unique identifier as a negative number to avoid conflicts with real job IDs
      // Use a smaller number that fits within int32 range (-2,147,483,648 to 2,147,483,647)
      const uniqueId = -(Math.floor(Date.now() / 1000) % 2000000000);
      const requestJobIds = [...jobIds, uniqueId];

      const request = {
        jobIds: requestJobIds,
      };
      const response = await MemoClientService.GenerateSummary(request);
      return response || { jobMemoSummaries: [] };
    },
    enabled: enabled && jobIds.length > 0,
  });
};

// Mutation for creating job memo summaries (more appropriate for generation)
export const useCreateJobMemoSummaryMutation = (
  opts?: Parameters<typeof useMutation<any, unknown, JobMemoSummaryParams, unknown>>['0'],
) => {
  return useMutation({
    mutationFn: async (params: JobMemoSummaryParams) => {
      // Add a unique identifier as a negative number to avoid conflicts with real job IDs
      // Use a smaller number that fits within int32 range
      const uniqueId = -(Math.floor(Date.now() / 1000) % 2000000000);
      const requestJobIds = [...params.jobIds, uniqueId];

      const request = {
        jobIds: requestJobIds,
        withoutLimit: 1,
      };
      const response = await MemoClientService.GenerateSummary(request);
      return response || { jobMemoSummaries: [] };
    },
    ...opts,
  });
};

export const useGetJobSummaryQuery = ({
  jobIds,
  enabled = true,
}: {
  jobIds: number[];
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.jobSummary.root, jobIds],
    queryFn: async () => {
      const request = {
        jobIds,
        withoutLimit: 1,
      };
      const response = await MemoClientService.GetJobSummary(request);
      return response || { jobMemoSummaries: [] };
    },
    enabled: enabled && jobIds.length > 0,
  });
};

export const useMemoReportQuery = ({
  req,
  enabled = true,
}: {
  req: CreateAiReportReq;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [queryKeys.memoReport.root, queryKeys.memoReport.getMemoReportHash(req)],
    queryFn: async () => {
      const response = await MemoClientService.CreateAiReport(req);
      return response || CreateAiReportResp.create();
    },
    enabled,
  });
};

/**
 * Rewrites a block of text with AI and returns the result. `eventId` and `memoIds` are
 * deliberately left unset so the server passes `input` straight through — no memos pulled
 * in, no `event.summary_id` write, no ai_response_report rows.
 */
export const useAiTextCleanupMutation = () => {
  return useMutation({
    mutationFn: async ({ promptId, input }: { promptId: string; input: string }) => {
      const response = await MemoClientService.CreateAiReport(
        CreateAiReportReq.create({ reportPromptId: promptId, input }),
      );
      return response?.textReport ?? '';
    },
  });
};
